import { format } from 'date-fns';
import { useState, useEffect, useRef, FormEvent, useContext } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Loader from '../components/UI/Loader';
import { deleteClientNote, getClientById, getClientNote, saveClientNote, searchClientNotes, updateClientNote } from '../services/clientsService';
import { ClientNote } from '../models/serviceModels/ClientNote';
import ApiErrorMessages from '../components/UI/ApiErrorMessages';
import TruncatedText from '../components/UI/TruncatedText';
import Input from '../components/UI/Input';
import Button from '../components/UI/Button';
import Dropdown from '../components/UI/Dropdown';
import { ADHOC_ID, CASE_PLAN_ID, CLIENT_FORM_ID, TRACKING_FORM_ID } from '../constants/clientNoteSourceType';
import Modal from '../components/UI/Modal';
import useElementVisible from '../hooks/useElementVisible';
import getclassNames from '../utils/getClassNames';
import FileUpload from '../components/UI/FileUpload';
import UploadableFile from '../models/pageModels/UploadableFile';
import { UPLOADED } from '../constants/fileUploadStatus';
import ErrorMessages from '../components/UI/ErrorMessages';
import { mapToUploadableFiles } from '../mappers/fileMapper';
import { executeSearch, getDefaultSearchFilters, getSearchFields } from '../services/searchService';
import { CLIENT_NOTES_SEARCH } from '../constants/searchType';
import Search, { SearchEvent } from '../components/UI/Search';
import DefaultSearchFilter from '../models/serviceModels/DeafultSearchFilter';
import SearchFieldReference from '../models/serviceModels/SearchFieldReference';

const ParticipantNote = () => {
    const { id = '', noteId = '' } = useParams();
    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState<ClientNote>();
    const [apiError, setApiError] = useState<any>();
    const [saving, setSaving] = useState(false);
    const [editingNote, setEditingNote] = useState(false);
    const [editingNoteFiles, setEditingNoteFiles] = useState<UploadableFile[]>([]);
    const [updatedNote, setUpdatedNote] = useState('');
    const [deletingNote, setDeletingNote] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [modalError, setModalError] = useState<any>();
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        setApiError(undefined);
        getClientNote({
            clientId: id, 
            noteId
        }).then(r => {
            setNote(r);
        }).catch(e => {
            setApiError(e);
            console.error(e);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleEditOnSave = () => {
        if (!id || !noteId || !note) {
            return;
        }

        setErrorMessages([]);

        if (!updatedNote && !editingNote) {
            setErrorMessages(['Note text or a file is required']);
            return;
        }

        setSaving(true);

        updateClientNote({
            id: noteId,
            clientId: id,
            value: updatedNote,
            files: editingNoteFiles.map(f => f.fileId!)
        }).then(r => {
            note.value = r.value;
            note.files = r.files;
            note.updated = r.updated;
            note.updatedBy = r.updatedBy;
            note.updatedByFirstName = r.createdByFirstName;
            note.updatedByLastName = r.updatedByLastName;

            setNote({
                ...note
            });

            handleEditingOnCancel();
        }).catch(e => {
            setApiError(e);
            console.error(e);
        }).finally(() => {
            setSaving(false);
        })
    };

    const handleEditingNote = () => {
        if (!note) {
            return;
        }

        setEditingNote(true);
        setEditingNoteFiles(mapToUploadableFiles(note.files || []));
        setUpdatedNote(note.value);
    };

    const handleEditingOnCancel = () => {
        setEditingNote(false);
        setEditingNoteFiles([]);
        setUpdatedNote('');
    };

    const handleConfirmDelete = () => {
        setModalError(undefined);
        setDeletingNote(true);
    };

    const handleOnDelete = () => {
        if (!id || !deletingNote) {
            return;
        }

        setModalError(undefined);
        setDeleting(true);

        deleteClientNote({
            clientId: id,
            id: noteId
        }).then(() => {
            navigate(`/participants/${id}/notes`, {
                replace: true
            });
        }).catch(e => {
            setModalError(e);
            console.error(e);
        }).finally(() => {
            setDeleting(false);
        });
    };

    const handleCancellingDelete = () => {
        setDeletingNote(false);
    }

    const getMainContent = () => {
        if (loading || !note) {
            return <Loader />;
        }

        const options = [];

        options.push({
            label: 'Edit',
            onClick: handleEditingNote
        });

        if (note.sourceId === ADHOC_ID) {
            options.push({
                label: 'Delete',
                onClick: handleConfirmDelete
            });
        } else if (note.sourceId === TRACKING_FORM_ID) {
            options.push({
                label: 'View Form',
                href: `/forms/${note.formId}/versions/${note.formVersionId}/instances/${note.formInstanceId}/view`
            });
        } else if (note.sourceId === CLIENT_FORM_ID) {
            options.push({
                label: 'View Participant',
                href: `/participants/${id}/profile-demographics`
            });
        } else if (note.sourceId === CASE_PLAN_ID) {
            options.push({
                label: 'View Plan',
                href: `/participants/${id}/plans/${note.casePlanId}`
            });
        }

        return (<>
            <ApiErrorMessages error={apiError} />
            <div className="row align-items-center">
                <div className="column-auto">
                    <h3>Note</h3>
                </div>
            </div>
            <div className="list-item-container">
                <div className="list-item">
                        {!editingNote && <>
                            <div className="row align-items-top">
                                <div className="column">
                                    {note.sourceId === CLIENT_FORM_ID && <div className="text-bold margin-bottom-2">Participant Additional Information</div>}
                                    {note.sourceId === TRACKING_FORM_ID && ((note.referenceName) || note.formName) && <div className="text-bold margin-bottom-2">{(<>{note.referenceName} - {note.sourceName}</>)}</div>}
                                    {note.sourceId === CASE_PLAN_ID && ((note.referenceName) || note.casePlanName) && <div className="text-bold margin-bottom-2">{note.referenceName} - Plan</div>}
                                    {note.value && <TruncatedText formatted={true}
                                        value={note.value} maxLength={100}
                                        lengthVariance={100} id={`${note.id}-note`} />}
                                    {!note.value && <span className="text-background text-italic">&lt;No note to display&gt;</span>}
                                </div>
                                <div className="column-auto">
                                    <Dropdown id={`options-dropdown-${note.id}`}
                                        options={options}
                                    />
                                </div>
                            </div>
                            <div className="row justify-content-between margin-top-5 text-sub">
                                <div className="column-auto">
                                    {note.updated ? format(new Date(note.updated), 'Pp') : format(new Date(note.created), 'Pp')}
                                </div>
                                <div className="column-auto">
                                    {note.updatedByFirstName ? `${note.updatedByFirstName} ${note.updatedByLastName}` : `${note.createdByFirstName} ${note.createdByLastName}`}
                                </div>
                            </div>
                            {note.files && note.files.length !== 0 && <div className="row margin-top-3">
                                <div className="column">
                                    <FileUpload id={`note-files-${note.id}`}
                                        value={mapToUploadableFiles(note.files)}
                                        disableRemove={true}
                                        disableUpload={true}
                                        label="Attachments" />
                                </div>
                            </div>}
                        </>}
                        {editingNote && <>
                            <ErrorMessages messages={errorMessages} />
                            <div className="row">
                                <div className="column">
                                    <Input type="multi"
                                        id="edit-note-input"
                                        value={updatedNote}
                                        label="Note"
                                        onChange={setUpdatedNote} />
                                </div>
                            </div>
                            <div className="row margin-top-2">
                                <div className="column">
                                    <FileUpload id="edit-files-upload"
                                        value={editingNoteFiles}
                                        onChange={setEditingNoteFiles}
                                        multiple={true} />
                                </div>
                            </div>
                            <div className="row justify-content-end margin-top-2">
                                <div className="column-auto">
                                    <Button id="cancel-save-note-button"
                                        onClick={handleEditingOnCancel}
                                        className="secondary"
                                        text="Cancel"
                                        disabled={saving} />
                                </div>
                                <div className="column-auto">
                                    <Button id="save-note-button"
                                        onClick={handleEditOnSave}
                                        text="Update Note"
                                        loading={saving}
                                        disabled={saving || editingNoteFiles.some(f => f.status !== UPLOADED)} />
                                </div>
                            </div>
                        </>
                        }
                    </div>
            </div>
        </>);

    };

    return (
        <section id="main-content" tabIndex={-1}>
            {getMainContent()}
            <Modal id="delete-note-modal" visible={deletingNote} className="small" onClose={handleCancellingDelete} title="Confirm Delete Note">
                {deletingNote && note && (
                    <div>
                        {modalError && <ApiErrorMessages error={modalError} />}
                        <div className="row">
                            <div className="column">
                                Are you sure you want to delete this note?
                                <br />
                                <br />
                                <TruncatedText id={`delete-note-${noteId}`} 
                                    formatted={true} 
                                    lengthVariance={100} 
                                    value={note.value} 
                                    maxLength={50} />
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-delete-note" text="Cancel" className="secondary" disabled={deleting} onClick={handleCancellingDelete} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-delete-note" text="Delete Note" className="warning" disabled={deleting} loading={deleting} onClick={handleOnDelete} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </section>);
};

export default ParticipantNote; 